import underApproval from "../../assets/illustrations/under-approval.png";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  Group,
  Select,
  Text,
  Rating,
  Tabs,
  Badge,
  Button,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { driversApi } from "../../api/drivers";
import { useOpenFromQuery } from "../../hooks/useOpenFromQuery";
import { rideTypesApi } from "../../api/rideTypes";
import { DriverOnboardModal } from "./DriverOnboardModal";
import type { Driver } from "../../types/domain";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { PageHeader } from "../../components/common/PageHeader";
import { SearchInput } from "../../components/common/SearchInput";
import { DataTable, type Column } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import { ExportMenu } from "../../components/common/ExportMenu";
import { TagChips, useTagCatalogue } from "../../components/tags/TagChips";
import { isActiveTag } from "../../api/tags";
import { collectAll, type ExportField } from "../../lib/export";
import { en } from "../../lib/format";
import { errorMessage } from "../../lib/axios";
import { S3Avatar } from "../../components/common/S3Avatar";
import { VehicleIcon } from "../../components/brand/VehicleIcon";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "underApproval", label: "Pending approval" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "suspended", label: "Suspended" },
];

const driverExportFields: ExportField<Driver>[] = [
  { label: "Name", value: (d) => d.personalDetails?.name },
  { label: "Phone", value: (d) => d.personalDetails?.phoneNumber },
  { label: "Email", value: (d) => d.personalDetails?.email },
  { label: "Make", value: (d) => d.vehicleDetails?.make },
  { label: "Model", value: (d) => d.vehicleDetails?.model },
  {
    label: "License Plate Number",
    value: (d) => d.vehicleDetails?.registrationNumber,
  },
  { label: "Rating", value: (d) => d.ratingDetails?.currentRating },
  { label: "Availability", value: (d) => d.tripAvailabilityStatus },
  { label: "Status", value: (d) => d.accountStatusDetails?.currentStatus },
  { label: "Joined", value: (d) => d.createdAt },
  {
    label: "Tags",
    value: (d) => (d.tags ?? []).filter(isActiveTag).map((t) => t.key).join("; "),
  },
];

export function DriversPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [onboardOpen, setOnboardOpen] = useState(false);
  useOpenFromQuery(() => setOnboardOpen(true));
  // Deep-linkable from the inbox / header search: ?tab=onboarding, ?status=underApproval.
  const [tab, setTab] = useState<"all" | "onboarding">(
    searchParams.get("tab") === "onboarding" ? "onboarding" : "all",
  );
  const [accountStatus, setAccountStatus] = useState(searchParams.get("status") ?? "");
  const [vehicleCategoryID, setVehicleCategoryID] = useState("");

  // Ride-type options for the filter.
  const rideTypes = useQuery({
    queryKey: ["ride-types", "active"],
    queryFn: () => rideTypesApi.list({ isActive: true }),
  });
  const rideTypeOptions = [
    { value: "", label: "All ride types" },
    ...(rideTypes.data?.items ?? []).map((rt) => ({
      value: rt._id,
      label: en(rt.name),
    })),
  ];

  // Count of drivers awaiting onboarding (badge on the tab) — documentPending
  // or underApproval.
  const onboardingCount = useQuery({
    queryKey: ["drivers-onboarding-count"],
    queryFn: () => driversApi.list({ onboarding: true, page: 1, limit: 1 }),
  });

  const [tag, setTag] = useState("");
  const listFilters =
    tab === "onboarding"
      ? { onboarding: true, vehicleCategoryID, tag }
      : { accountStatus, vehicleCategoryID, tag };
  const { data: tagCatalogue } = useTagCatalogue();
  const tagOptions = [
    { value: "", label: "All tags" },
    ...(tagCatalogue ?? [])
      .filter((t) => t.appliesTo.includes("driver"))
      .map((t) => ({ value: t.key, label: t.archived ? `${t.label} (archived)` : t.label })),
  ];

  const { page, setPage, search, onSearchChange, result, isLoading, error } =
    usePaginatedList<Driver>({
      queryKey: ["drivers", tab],
      fetcher: driversApi.list,
      filters: listFilters,
    });

  const columns: Column<Driver>[] = [
    {
      key: "name",
      header: "Driver",
      render: (d) => (
        <Group gap="sm" wrap="nowrap">
          <S3Avatar src={d.personalDetails?.profilePicUrl} radius="xl" size={36} alt={d.personalDetails?.name}>
            {d.personalDetails?.name?.charAt(0)?.toUpperCase() ?? "D"}
          </S3Avatar>
          <div>
            <Text size="sm" fw={600} lh={1.2}>
              {d.personalDetails?.name ?? "—"}
            </Text>
            <Text size="xs" c="dimmed">
              {d.personalDetails?.phoneNumber ?? "—"}
            </Text>
            <TagChips tags={d.tags} />
          </div>
        </Group>
      ),
    },
    {
      key: "vehicle",
      header: "Vehicle",
      render: (d) =>
        d.vehicleDetails
          ? `${d.vehicleDetails.make ?? ""} ${d.vehicleDetails.model ?? ""}`.trim() || "—"
          : "—",
    },
    {
      key: "category",
      header: "Category",
      render: (d) => {
        const rt = rideTypes.data?.items.find(
          (r) => r._id === d.vehicleDetails?.vehicleCategoryID,
        );
        if (!rt) return <Text size="xs" c="dimmed">{d.vehicleDetails?.vehicleCategoryID ? "Unknown" : "—"}</Text>;
        return (
          <Group gap="xs" wrap="nowrap">
            <VehicleIcon rideType={en(rt.name)} latraType={d.vehicleDetails?.latraVehicleType} size={24} />
            <Text size="sm">{en(rt.name)}</Text>
          </Group>
        );
      },
    },
    {
      key: "rating",
      header: "Rating",
      render: (d) =>
        d.ratingDetails?.currentRating ? (
          <Rating value={d.ratingDetails.currentRating} fractions={2} readOnly size="xs" />
        ) : (
          <Text size="xs" c="dimmed">—</Text>
        ),
    },
    {
      key: "availability",
      header: "Availability",
      render: (d) => <StatusBadge status={d.tripAvailabilityStatus} />,
    },
    {
      key: "status",
      header: "Status",
      render: (d) => <StatusBadge status={d.accountStatusDetails?.currentStatus} />,
    },
  ];

  const onboardingTotal = onboardingCount.data?.total ?? 0;

  return (
    <>
      <PageHeader
        title="Drivers"
        subtitle="Onboarding, approval & fleet management"
        actions={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setOnboardOpen(true)}
          >
            Add driver
          </Button>
        }
      />

      <Tabs value={tab} onChange={(v) => { setTab((v as "all" | "onboarding") ?? "all"); setPage(1); }} mb="md">
        <Tabs.List>
          <Tabs.Tab value="all">All drivers</Tabs.Tab>
          <Tabs.Tab
            value="onboarding"
            rightSection={
              onboardingTotal > 0 ? (
                <Badge size="sm" circle color="red" variant="filled">
                  {onboardingTotal}
                </Badge>
              ) : null
            }
          >
            Onboarding
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Card withBorder radius="md" padding="lg">
        <Group mb="md" gap="sm">
          <SearchInput value={search} onChange={onSearchChange} placeholder="Search name, email, phone…" />
          {tab === "all" && (
            <Select
              data={STATUS_OPTIONS}
              value={accountStatus}
              onChange={(v) => { setAccountStatus(v ?? ""); setPage(1); }}
              w={180}
              allowDeselect={false}
            />
          )}
          <Select
            data={rideTypeOptions}
            value={vehicleCategoryID}
            onChange={(v) => { setVehicleCategoryID(v ?? ""); setPage(1); }}
            w={180}
            allowDeselect={false}
            placeholder="Ride type"
          />
          {tagOptions.length > 1 && (
            <Select
              data={tagOptions}
              value={tag}
              onChange={(v) => { setTag(v ?? ""); setPage(1); }}
              w={170}
              allowDeselect={false}
              placeholder="Tag"
            />
          )}
          <ExportMenu
            filename="drivers"
            fields={driverExportFields}
            fetchAll={() =>
              collectAll(driversApi.list, {
                search: search || undefined,
                ...listFilters,
              })
            }
          />
        </Group>

        <DataTable
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(d) => d._id}
          loading={isLoading}
          error={error ? errorMessage(error) : null}
          emptyLabel={tab === "onboarding" ? "No drivers awaiting onboarding" : "No drivers found"}
          emptyImage={tab === "onboarding" ? underApproval : undefined}
          onRowClick={(d) => navigate(`/drivers/${d._id}`)}
          page={page}
          totalPages={result?.totalPages}
          total={result?.total}
          onPageChange={setPage}
        />
      </Card>

      <DriverOnboardModal
        opened={onboardOpen}
        onClose={() => setOnboardOpen(false)}
        onDone={() => {
          setTab("onboarding");
          void qc.invalidateQueries({ queryKey: ["drivers"] });
          void qc.invalidateQueries({ queryKey: ["drivers-onboarding-count"] });
        }}
      />
    </>
  );
}
